/* eslint-disable camelcase */
import * as React from "react"
import * as yup from "yup"
import { Appearance, TextInput } from "react-native"
import { FieldArray, FieldArrayRenderProps, Formik, FormikProps, FormikValues } from "formik"
import { ModelField } from "../field/model-field/model-field"
import { Text } from "../text/text"
import { Divider, View } from "native-base"
import { color } from "../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../i18n"
import { FC, useCallback, useMemo, useRef } from "react"
// import { Button } from "../../button/button"
import { NumeralField } from "../field/numeral-field/numeral-field"
import { RadioField } from "../field/radio-field/radio-field"
import { useStores } from "../../models"
import _ from "lodash"
import { DateTimePickerField } from "../field/date-time-picker-field/date-time-picker-field"
import { Cycle, Farm } from "../../database"
import moment from "moment"
import { FormProps } from "../form/form"
import { JalaIcon } from "../jala-icon/jala-icon"

export interface AutoFeederPondDailyFeedCreateMultipleFormProps extends FormProps {
  initialValues?: {
    farm_id?: number,
    cycle_id?: number,
    feed_at?: string,
    logged_date?: string,
    fasting?: boolean,
    models?: Array<{
      logged_at?: string,
      logged_time?: string,
      quantity?: number
    }>
  },
  autoFeederPond?: any
}

const AutoFeederDailyFeedForm: FC<{
  colorScheme?: "light"|"dark",
  values: any,
  index: number,
  autoFeederPond?: any,
  setFieldTouched?: (field?: string, isTouched?: boolean) => void,
  setFieldValue?: (field?: string, value?: any) => void,
  autoFeeders?: any
} & FieldArrayRenderProps> = observer(function PondCreateForm(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    name,
    values,
    index,
    autoFeederPond,
    setFieldTouched,
    setFieldValue,
    autoFeeders
  } = props;

  const input = {
    [`${name}.${index}.quantity`]: React.useRef<TextInput>(),
  }

  const nextInput = {
    [`${name}.${index}.quantity`]: () => true,
  }

  const convertToRoman = (num) => {
    if(!num || num < 1){ return "";}
    if(num >= 10){ return "X" + convertToRoman(num - 10);}
    if(num >= 9){ return "IX" + convertToRoman(num - 9);}
    if(num >= 5){ return "V" + convertToRoman(num - 5);}
    if(num >= 4){ return "IV" + convertToRoman(num - 4);}
    if(num >= 1){ return "I" + convertToRoman(num - 1);}
  }

  const unitElement = () => <View pr="8px">
    <Text text="kg" preset="title4" color={color.palette.gray800} />
  </View>

  return <>
    <View flexDirection="row" alignItems="center" px="16px" pt="12px">
      <JalaIcon name="feeds" color={color.primary} size={20} />
      <Text text={`${translate("autoFeederDailyFeedForm.quantity")} ${convertToRoman(index+1)}`} preset="title4" color={color.primary} />
    </View>
    <View flexDirection="row">
      <View flex={1.5} flexDirection="row">
        <DateTimePickerField
          name={`${name}.${index}.logged_time`}
          txPrefix="autoFeederDailyFeedForm"
          colorScheme={colorScheme}
          formControlProps={{
            flex: 1,
            paddingRight: 0,
            labelTx: "autoFeederDailyFeedForm.start",
            labelStyle: { width: "100%", textAlign: "center" }
          }}
          dateTimePickerProps={{
            dateTimePickerProps: { mode: "time" },
            isDisabled: autoFeederPond ? autoFeederPond.state?.mode === 'continuous' : undefined,
            width: '100%'
          }}
        />
        <DateTimePickerField
          key={`${values.cycle_id}-${index}-${values.logged_time}-end`}
          name={`${name}.${index}.end`}
          txPrefix="autoFeederDailyFeedForm"
          colorScheme={colorScheme}
          formControlProps={{
            flex: 1,
            paddingRight: 0,
            labelTx: "autoFeederDailyFeedForm.end",
            labelStyle: { width: "100%", textAlign: "center" }
          }}
          dateTimePickerProps={{
            dateTimePickerProps: { mode: "time" },
            isDisabled: true,
            value: autoFeederPond && autoFeederPond.state?.mode === 'continuous'
              ? (autoFeederPond.state?.endTime && moment(autoFeederPond.state.endTime, 'HH:mm:ss').format('HH:mm:ss') || null)
              : (values.logged_time && moment(values.logged_time, 'HH:mm:ss').add(autoFeederPond?.state?.intervalPeriod || 60, 'minutes').format('HH:mm:ss') || null),
            width: '100%'
          }}
        />
      </View>
      <NumeralField
        name={`${name}.${index}.quantity`}
        txPrefix="autoFeederDailyFeedForm"
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{
          // isRequired: true,
          flex: 1,
          inputProps: { placeholder: '-', InputRightElement: unitElement() }
        }}
        numeralInputProps={{
          onChangeNumber: async (value) => {
            setFieldValue(`${name}.${index}.quantity`, value || null)
            setFieldTouched(`${name}.${index}.quantity`)
            if (value != null && autoFeeders.length> 1) {
              setFieldValue(`autoFeeders${[0]}.feedRatio`, value / 2)
              setFieldValue(`autoFeeders${[1]}.feedRatio`, value / 2)
            }
          }
        }}
      />
    </View>
  </>
})

const FormikField: FC<{ colorScheme?: "light"|"dark", autoFeederPond?: any } & FormikProps<any>> = observer(function FormikField(props) {
  const { colorScheme, initialValues, values, setFieldValue, setFieldTouched, autoFeederPond } = props
  const { appStore: { farmStore } } = useStores()

  const renderFieldArray = useCallback((props: FieldArrayRenderProps) => {
    const {
      form: { values }
    } = props

    return <>
      {(values.models || []).map((model, index) => <View key={`${values.cycle_id}-${index}`}>
        <AutoFeederDailyFeedForm
          colorScheme={colorScheme}
          values={model}
          index={index}
          autoFeederPond={autoFeederPond}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          autoFeeders={values.autoFeeders || []}
          {...props}
        />
      </View>)}
    </>
  }, [autoFeederPond, colorScheme, setFieldTouched, setFieldValue])

  const unitElement = () => <View pr="8px">
    <Text text="kg" preset="title4" color={color.palette.gray800} />
  </View>

  const renderFieldArrayRatio = useCallback((props: FieldArrayRenderProps) => {
    const {
      form: { values }
    } = props

    const totalFeed = values.models && values.models.reduce((accumulator, currentValue) => {
      return accumulator + currentValue.quantity;
    }, 0);

    if (!values.autoFeeders || !values.autoFeeders.length)
      return null

    return <>
      <Text tx="autoFeederDailyFeedForm.ratio" preset="title5" color={color.palette.gray800} pl="16px" />
      <View flexDirection={"row"} justifyContent="space-between" px="16px">
        {(values.autoFeeders || []).map((model, index) => {
        return ( <View flex={1} mr={index === 0 ? 6 : 0} key={`${values.cycle_id}-${index}`}>
          <NumeralField
            name={`autoFeeders${[index]}.feedRatio`}
            colorScheme={colorScheme}
            // input={input}
            // nextInput={nextInput}
            formControlProps={{
              isDisabled: index !== 0 || totalFeed === 0,
              // isRequired: true,
              label: model.name,
              flex: 1,
              style: {
                // marginBottom: 8,
                paddingBottom: 8,
                backgroundColor: color.palette.white
              },
              inputProps: {
                placeholder: '-',
                InputRightElement: unitElement(),
              }
            }}
            numeralInputProps={{
              onChangeNumber: async (value) => {
                const val = value < totalFeed ? value : totalFeed
                await setFieldValue(`autoFeeders${[0]}.feedRatio`, val || null)
                await setFieldTouched(`autoFeeders${[0]}.feedRatio`)
                await setFieldValue(`autoFeeders${[1]}.feedRatio`, totalFeed - val)
              }
            }}
          />
        </View>)})}
      </View>
    </>
  }, [colorScheme, setFieldTouched, setFieldValue])

  return <>
    <ModelField
      name="farm_id"
      txPrefix="feedForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true }}
      modelPickerProps={{
        Model: Farm,
        isDisabled: true,
        onValueChange: async (value) => {
          await setFieldValue("cycle_id", null)
          await setFieldValue("farm_id", value || null)
          await setFieldTouched("farm_id")
        },
      }}
    />
    <Divider />
    <ModelField
      key={`${values.farm_id}-cycle_id`}
      name="cycle_id"
      txPrefix="feedForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true }}
      modelPickerProps={{
        Model: Cycle,
        dataLabel: "pond_name",
        fetchData: (model, search) => (
          Promise.resolve(_.orderBy(
            farmStore.cycles.map((cycle) => Cycle.hydrate(cycle)), ['started_at', 'pond_name_alpha_numeric'], ['desc', 'asc']
          )).then((models) => models.filter((m) => (
            `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
          ).toLowerCase().includes((search || '').toLowerCase())))
        ),
        renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
        clearable: true,
        isDisabled: !values.farm_id || !!initialValues.cycle_id,
        titleTx: "feedForm.cycleIdPlaceholder"
      }}
    />
    <DateTimePickerField
      name="logged_date"
      txPrefix="feedForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true }}
      dateTimePickerProps={{
        value: initialValues.logged_date,
        isDisabled: !!initialValues.logged_date
      }}
    />
    <RadioField
      name="fasting"
      txPrefix="feedForm"
      colorScheme={colorScheme}
      radioGroupProps={{
        defaultValue: JSON.stringify(initialValues.fasting),
      }}
      radios={[{
        value: JSON.stringify(true),
        children: <Text tx="Yes" preset="body2" ml="6px" colorScheme={colorScheme} />
      }, {
        value: JSON.stringify(false),
        children: <Text tx="No" preset="body2" ml="6px" colorScheme={colorScheme} />
      }]}
      castValue={JSON.parse}
      isHorizontal
    />
    <View paddingX="16px" mb="5px">
      <Text tx="autoFeederDailyFeedForm.subtitle" preset="title3" colorScheme={colorScheme} />
    </View>
    <Divider height={1} backgroundColor={color.palette.background} />
    <FieldArray
      name="models"
      render={renderFieldArray}
    />
    <FieldArray
      name="autoFeeders"
      render={renderFieldArrayRatio}
    />
    {/* <TextField */}
    {/*   name="remark" */}
    {/*   txPrefix="autoFeederDailyFeedForm" */}
    {/*   colorScheme={colorScheme} */}
    {/*   // input={input} */}
    {/*   // nextInput={nextInput} */}
    {/*   formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }} */}
    {/* /> */}
  </>
})

/**
 * Describe your component here
 */
export const AutoFeederPondDailyFeedCreateMultipleForm = observer(function AutoFeederPondDailyFeedCreateMultipleForm(props: AutoFeederPondDailyFeedCreateMultipleFormProps) {
  const {
    initialValues: initialValuesProps = {}, onSubmit:onSubmitProps, colorScheme = "light" || Appearance.getColorScheme(), innerRef: innerRefProps,
    autoFeederPond,
    ...rest
  } = props

  const modelForm = useRef<FormikProps<FormikValues>>()
  const innerRef = useCallback((node) => {
    modelForm.current = node;
    if (innerRefProps instanceof Function)
      innerRefProps(node);
  }, [innerRefProps])

  const validationSchema = useMemo(() => yup.object({
    models: yup.array().of(yup.object({
      quantity: yup.number().min(0.1).nullable().label(translate("autoFeederDailyFeedForm.quantity")),
    })).nullable().required(),
  }), [])

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      models: values.models.flatMap((m) => {
        if (m.ids?.length) {
          if (autoFeederPond && autoFeederPond.state?.mode === 'continuous')
            return m.ids.map((id, index) => ({
              id,
              quantity: m.quantity * m.quantities_ratio[index],
              fasting: values.fasting,
              cycle_id: values.cycle_id,
              get _action() { return !this.quantity ? 'delete' : undefined }
            }))
          return m.ids.map((id, index) => ({
            id,
            quantity: m.quantity * m.quantities_ratio[index],
            logged_at: values.logged_date
              && m.logged_time
              && moment(`${values.logged_date} ${m.logged_time}`).utc().format("YYYY-MM-DD HH:mm:ss")
              || null,
            fasting: values.fasting,
            cycle_id: values.cycle_id,
            get _action() { return !this.quantity ? 'delete' : undefined }
          }))
        }
        return {
          quantity: m.quantity,
          logged_at: values.logged_date
            && m.logged_time
            && moment(`${values.logged_date} ${m.logged_time}`).utc().format("YYYY-MM-DD HH:mm:ss")
            || null,
          fasting: values.fasting,
          cycle_id: values.cycle_id,
          remark: '#AutoFeeder'
        }
      }).filter((m) => m.id || m.quantity)
    })
  }, [autoFeederPond, onSubmitProps])

  const initialValues = useMemo<AutoFeederPondDailyFeedCreateMultipleFormProps["initialValues"]>(() => ({
    ...initialValuesProps,
    logged_date: initialValuesProps.feed_at || moment().format('YYYY-MM-DD'),
    models: [
      ...initialValuesProps.models.map((m) => {
        return {
          ...m,
          logged_at: m.logged_at || null,
          logged_time: m.logged_at && moment.utc(m.logged_at).local().format("HH:mm:ss")
        }
      }),
    ],
  }), [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {(formikProps) => (
        <FormikField colorScheme={colorScheme} {...formikProps} autoFeederPond={autoFeederPond} />
      )}
    </Formik>
  )
})
